import React, { useMemo, useState } from 'react';
import { BodyText, Box } from 'vopay';
import { Trans, useTranslation } from 'react-i18next';
import { withStyles } from 'vopay';
import { useLocation, useHistory } from 'react-router-dom';
import { Button, UtilButton, Complete } from '../../UI';
import styles from './ConvertMyTemplateConfirm.module.scss';
import useFileConversion from '@/hooks/useApi/useFileConversion';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useDataFetchIndicator } from '@/hooks';
import { useLockedFeatures } from '@/providers/LockedFeatures.provider';
import { useLockedFeatureDialogue } from '@/providers/LockedFeatureDialogue.provider';

const classes = withStyles(styles);

const ConvertMyTemplateConfirm: React.FC = () => {
  const { t } = useTranslation('translations', { keyPrefix: 'FileUpload.ConvertMyTemplateConfirm' });
  const { t: tHeader } = useTranslation('translations', { keyPrefix: 'FileUpload.ConvertMyTemplateConfirm.Header' });
  const location = useLocation();
  const history = useHistory();
  const { downloadFile, submitFile } = useFileConversion();
  const alertDialog = useAlertDialog();
  const [, setLoading] = useDataFetchIndicator();
  const { updateIsLocked } = useLockedFeatureDialogue();
  const lockedFeatures = useLockedFeatures();
  const [hasBeenProcessed, setHasBeenProcessed] = useState(false);
  const [numberOfFailedTransactions, setNumberOfFailedTransactions] = useState(0);

  // Get details from query params
  const queryParams = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      transferType: params.get('transferType') || '',
      paymentRail: params.get('paymentRail') || '',
      currency: params.get('currency') || 'CAD',
      uploadFile: params.get('uploadFile') || '',
      numberOfTransactions: parseInt(params.get('numberOfTransactions') || '0'),
      totalAmount: params.get('totalAmount'),
      convertToBank: params.get('convertToBank') || '',
      valueDate: params.get('valueDate') || '',
      FallbackPaymentRail: params.get('FallbackPaymentRail') || '',
      fileConversionID: params.get('fileConversionID') || '',
      targetFileName: params.get('targetFileName') || '',
    };
  }, [location.search]);

  const convertBankAbbrev = (abbrev: string) => {
    switch (abbrev.toLowerCase()) {
      case 'boa':
        return 'Bank of America';
      case 'gs':
        return 'Goldman Sachs';
      default:
        return abbrev;
    }
  };

  const convertPaymentRailLabel = (value: string) => {
    switch (value.toLowerCase()) {
      case 'ach':
        return 'ACH';
      case 'rtp':
        return 'RTP';
      case 'wire':
        return 'Fedwire';
      case 'eft':
        return 'EFT';
      case 'vopayinstant':
        return 'VopayInstant';
      default:
        return value;
    }
  };
  const convertToBank = useMemo(() => {
    return convertBankAbbrev(queryParams.convertToBank);
  }, [queryParams.convertToBank]);

  const details = useMemo(
    () => [
      {
        label: tHeader('transferType'),
        value: t(`transferTypeOptions.${queryParams.transferType}`),
      },
      {
        label: tHeader('paymentRail'),
        value: convertPaymentRailLabel(queryParams.paymentRail),
      },
      {
        label: tHeader('currency'),
        value: queryParams.currency,
      },
      {
        label: tHeader('uploadFile'),
        value: queryParams.uploadFile,
      },
      {
        label: tHeader('numberOfTransactions'),
        value: queryParams.numberOfTransactions,
      },
      {
        label: tHeader('totalAmount'),
        value: `$${queryParams.totalAmount} ${queryParams.currency}`,
      },
      {
        label: tHeader('convertToBank'),
        value: convertToBank,
      },
      {
        label: tHeader('valueDate'),
        value: queryParams.valueDate,
      },
      {
        label: tHeader('fallbackPaymentRail'),
        value: convertPaymentRailLabel(queryParams.FallbackPaymentRail),
      },
    ],
    [queryParams],
  );

  const handleDownloadConvertedFile = async () => {
    if (!queryParams.fileConversionID) {
      alertDialog.error(t('downloadError'), t('noFileConversionId'));
      return;
    }

    try {
      setLoading(true);
      await downloadFile({
        FileConversionID: queryParams.fileConversionID,
        FileType: queryParams.convertToBank,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('downloadFailed');
      alertDialog.error(t('downloadError'), msg);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadNewFile = () => {
    history.push('/file-upload-history', { openDrawer: true });
  };
  const handleGoToTransactionHistory = () => {
    history.push('/file-upload-history');
  };
  const handleProcessDirectly = async (selectedBank: string) => {
    const result = await alertDialog.open(
      t('processDialog.title', {
        bank: selectedBank,
      }),
      <>
        <BodyText className={classes('processDialogDescription')}>
          {t('processDialog.description', {
            bank: selectedBank,
          })}
        </BodyText>{' '}
        <BodyText className={classes('processDialogDescription')}>{t('processDialog.warning')}</BodyText>
      </>,
      {
        confirmLabel: t('processDialog.confirmLabel'),
        abortLabel: t('processDialog.cancelLabel'),
      },
    );
    if (result === 'confirm') {
      try {
        setLoading(true);
        const res = await submitFile({
          FileConversionID: queryParams.fileConversionID,
        });

        if (!res.Success) {
          throw new Error(res.ErrorMessage);
        }
        setNumberOfFailedTransactions(res.TotalFailed);
        setHasBeenProcessed(true);
      } catch (err) {
        const msg = err instanceof Error ? err.message : t('submitFailed');
        alertDialog.error(t('submitError'), msg);
        console.error('File submission failed:', err);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className={classes('container')}>
      <div className={classes('completeWrapper')}>
        <Complete
          title={t('title')}
          showAmounts={false}
          details={details}
          header={
            <div className={classes('header')}>
              <UtilButton
                className={classes('homeButton')}
                iconLeft="chevron_left"
                size="large"
                onClick={() => history.push('/file-upload-history')}
              >
                {t('home')}
              </UtilButton>
            </div>
          }
        >
          <Box gap="md" width="100%" jc="sb" mt="sm">
            {!hasBeenProcessed && (
              <>
                <Button
                  onClick={() => {
                    const isLocked = !lockedFeatures.FileSubmissionEnabled;
                    if (isLocked) {
                      updateIsLocked(true, t('fileSubmission'));
                    } else {
                      handleProcessDirectly(convertToBank);
                    }
                  }}
                  className={classes('button')}
                  size="small"
                  colorScheme="secondary"
                  iconRight={!lockedFeatures.FileSubmissionEnabled ? 'lock' : undefined}
                >
                  {t('processDirectly', { bank: convertToBank })}
                </Button>
                <Button
                  colorScheme="primary"
                  onClick={handleDownloadConvertedFile}
                  className={classes('button')}
                  size="small"
                >
                  {t('downloadConvertedFile')}
                </Button>
              </>
            )}
            {hasBeenProcessed && (
              <div className={classes('processedActions')}>
                {numberOfFailedTransactions !== 0 && (
                  <BodyText className={classes('failedTransactionsInfo')}>
                    <Trans
                      i18nKey="FileUpload.ConvertMyTemplateConfirm.fileProcessedWithFailuresMessage"
                      values={{ failedCount: numberOfFailedTransactions }}
                      components={{ bold: <strong /> }}
                    />
                  </BodyText>
                )}
                <div className={classes('actionsButtons')}>
                  <Button
                    onClick={handleUploadNewFile}
                    className={classes('button')}
                    size="small"
                    colorScheme="secondary"
                  >
                    {t('uploadNewFile')}
                  </Button>
                  <Button
                    colorScheme="primary"
                    onClick={handleGoToTransactionHistory}
                    className={classes('button')}
                    size="small"
                  >
                    {t('goToTransactionHistory')}
                  </Button>
                </div>
              </div>
            )}
          </Box>
        </Complete>
      </div>
    </div>
  );
};

export default ConvertMyTemplateConfirm;
